import { FC, ReactNode, useRef, useCallback, KeyboardEvent } from 'react';
import { makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { useWbTabsContext } from './WbTabsListContext';

const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      borderRadius: 14,
      backgroundColor: theme.palette.blueGrey[50],
      padding: theme.spacing(0.25),
      position: 'relative',
      gap: theme.spacing(0.5),
      height: 28,
    },
    indicator: {
      position: 'absolute' as const,
      backgroundColor: theme.palette.common.white,
      borderRadius: 12,
      boxShadow: theme.shadows[1],
      zIndex: 0,
      transition:
        'left 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
      willChange: 'left, width',
      height: 24,
      top: 2,
      left: 'anchor(left)',
      width: 'anchor-size(width)',
    },
    indicatorHidden: {
      opacity: 0,
    },
  }),
  { name: 'WbTabsList' },
);

interface WbTabsListProps {
  children: ReactNode;
  'aria-label': string;
  className?: string;
}

export const WbTabsList: FC<WbTabsListProps> = ({
  children,
  'aria-label': ariaLabel,
  className,
}) => {
  const classes = useStyles();
  const { value, onValueChange, instanceId } = useWbTabsContext();
  const containerRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      const container = containerRef.current;
      if (!container) return;

      const tabs = Array.from(
        container.querySelectorAll<HTMLButtonElement>(
          '[role="tab"]:not([disabled])',
        ),
      );
      if (tabs.length === 0) return;

      const currentIndex = tabs.findIndex(
        tab => tab.getAttribute('data-value') === value,
      );

      let nextIndex: number | undefined;

      if (e.key === 'ArrowRight') {
        nextIndex = (currentIndex + 1) % tabs.length;
      } else if (e.key === 'ArrowLeft') {
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      } else if (e.key === 'Home') {
        nextIndex = 0;
      } else if (e.key === 'End') {
        nextIndex = tabs.length - 1;
      }

      if (nextIndex !== undefined) {
        e.preventDefault();
        const nextTab = tabs[nextIndex];
        const nextValue = nextTab.getAttribute('data-value');
        if (nextValue) {
          onValueChange(nextValue);
          nextTab.focus();
        }
      }
    },
    [value, onValueChange],
  );

  return (
    <div
      ref={containerRef}
      className={clsx(classes.root, className)}
      role="tablist"
      aria-label={ariaLabel}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
    >
      {children}
      <div
        role="presentation"
        className={clsx(classes.indicator, {
          [classes.indicatorHidden]: !value,
        })}
        style={
          {
            positionAnchor: `--wb-tab-${instanceId}-${value}`,
          } as React.CSSProperties
        }
      />
    </div>
  );
};
